/**
 * Upload Page
 *
 * Allows customers to select files and upload them to a specific audit project
 * using the multipart upload engine. Supports drag-and-drop, multi-file queues,
 * per-file progress tracking, cancel, and retry.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, UploadCloud, Shield, FileText, HardDrive, CheckCircle2 } from 'lucide-react';
import { FileDropZone, UploadProgressCard, Skeleton } from '@connor-music/shared';
import { customerApiClient } from '@/services/api';
import {
  MultipartUploadEngine,
  type UploadProgress,
  type UploadStatus,
} from '@/services/uploadEngine';
import type { Project } from '@shared/types';

// ---------------------------------------------------------------------------
// Local state types
// ---------------------------------------------------------------------------

interface UploadEntry {
  /** Engine upload ID (assigned once upload starts) */
  engineId: string | null;
  /** Original File reference for retry */
  file: File;
  status: UploadStatus;
  progress: UploadProgress;
}

const INITIAL_PROGRESS: UploadProgress = {
  loaded: 0,
  total: 0,
  speed: 0,
  eta: 0,
  percent: 0,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Upload() {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [isLoadingProject, setIsLoadingProject] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploads, setUploads] = useState<Map<string, UploadEntry>>(new Map());
  const [isStarted, setIsStarted] = useState(false);

  const engineRef = useRef<MultipartUploadEngine | null>(null);
  // Counter used to generate stable keys for queued files before they have engine IDs
  const keyCounterRef = useRef(0);

  // ---------------------------------------------------------------------------
  // Load project metadata
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const p = await customerApiClient.getProject(projectId);
        if (!cancelled) setProject(p);
      } catch (err) {
        console.error('Failed to load project:', err);
      } finally {
        if (!cancelled) setIsLoadingProject(false);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // ---------------------------------------------------------------------------
  // Create the upload engine (once)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const engine = new MultipartUploadEngine({
      concurrency: 4,
      retries: 3,
      onProgress: (uploadId: string, progress: UploadProgress) => {
        setUploads((prev) => {
          const next = new Map(prev);
          const entry = findEntryByEngineId(next, uploadId);
          if (entry) {
            entry.progress = progress;
            entry.status = 'uploading';
          }
          return next;
        });
      },
      onComplete: (uploadId: string) => {
        setUploads((prev) => {
          const next = new Map(prev);
          const entry = findEntryByEngineId(next, uploadId);
          if (entry) {
            entry.status = 'complete';
            entry.progress = { ...entry.progress, percent: 100 };
          }
          return next;
        });
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onError: (uploadId: string, _error: Error) => {
        setUploads((prev) => {
          const next = new Map(prev);
          const entry = findEntryByEngineId(next, uploadId);
          if (entry) {
            entry.status = 'failed';
          }
          return next;
        });
      },
    });

    engineRef.current = engine;

    return () => {
      engine.destroy();
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function findEntryByEngineId(
    map: Map<string, UploadEntry>,
    engineId: string,
  ): UploadEntry | undefined {
    for (const entry of map.values()) {
      if (entry.engineId === engineId) return entry;
    }
    return undefined;
  }

  const generateKey = useCallback(() => {
    keyCounterRef.current += 1;
    return `file-${keyCounterRef.current}`;
  }, []);

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      const newFiles = [...selectedFiles, ...files];
      setSelectedFiles(newFiles);

      // Add entries to the upload map with pending status
      setUploads((prev) => {
        const next = new Map(prev);
        for (const file of files) {
          const key = generateKey();
          next.set(key, {
            engineId: null,
            file,
            status: 'pending',
            progress: { ...INITIAL_PROGRESS, total: file.size },
          });
        }
        return next;
      });
    },
    [selectedFiles, generateKey],
  );

  const handleUploadAll = useCallback(async () => {
    if (!projectId || !engineRef.current) return;
    setIsStarted(true);

    const engine = engineRef.current;
    const entriesToStart: [string, UploadEntry][] = [];

    for (const [key, entry] of uploads.entries()) {
      if (entry.status === 'pending') {
        entriesToStart.push([key, entry]);
      }
    }

    for (const [key, entry] of entriesToStart) {
      try {
        const engineId = await engine.uploadFile(projectId, entry.file);
        setUploads((prev) => {
          const next = new Map(prev);
          const current = next.get(key);
          if (current) {
            current.engineId = engineId;
            current.status = 'uploading';
          }
          return next;
        });
      } catch (err) {
        console.error(`Failed to start upload for ${entry.file.name}:`, err);
        setUploads((prev) => {
          const next = new Map(prev);
          const current = next.get(key);
          if (current) {
            current.status = 'failed';
          }
          return next;
        });
      }
    }
  }, [projectId, uploads]);

  const handleCancel = useCallback(
    (key: string) => {
      const entry = uploads.get(key);
      if (!entry || !entry.engineId || !engineRef.current) return;
      engineRef.current.cancelUpload(entry.engineId);
      setUploads((prev) => {
        const next = new Map(prev);
        const current = next.get(key);
        if (current) {
          current.status = 'aborted';
        }
        return next;
      });
    },
    [uploads],
  );

  const handleRetry = useCallback(
    async (key: string) => {
      const entry = uploads.get(key);
      if (!entry || !projectId || !engineRef.current) return;

      const engine = engineRef.current;

      try {
        const engineId = await engine.uploadFile(projectId, entry.file);
        setUploads((prev) => {
          const next = new Map(prev);
          const current = next.get(key);
          if (current) {
            current.engineId = engineId;
            current.status = 'uploading';
            current.progress = { ...INITIAL_PROGRESS, total: entry.file.size };
          }
          return next;
        });
      } catch (err) {
        console.error(`Retry failed for ${entry.file.name}:`, err);
      }
    },
    [uploads, projectId],
  );

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const hasPendingFiles = Array.from(uploads.values()).some(
    (e) => e.status === 'pending',
  );

  const hasActiveUploads = Array.from(uploads.values()).some(
    (e) => e.status === 'uploading',
  );

  const allComplete = uploads.size > 0 && Array.from(uploads.values()).every(
    (e) => e.status === 'complete',
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!projectId) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 py-12">
        No project specified.
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </Link>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Submit Audit Data
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {isLoadingProject
            ? <Skeleton className="h-4 w-64 inline-block" />
            : project
              ? <>Upload reports and supporting documentation for <span className="font-medium text-gray-700 dark:text-gray-300">{project.name}</span></>
              : 'Unknown project'}
        </p>
      </div>

      {/* Drop zone card */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden mb-6">
        <div className="p-6">
          <FileDropZone
            onFilesSelected={handleFilesSelected}
            disabled={hasActiveUploads}
          />
        </div>
        <div className="border-t border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-800/50 px-6 py-3">
          <div className="flex items-center justify-center gap-6 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              CSV, XLSX, PDF, and more
            </span>
            <span className="flex items-center gap-1.5">
              <HardDrive className="h-3.5 w-3.5" />
              Up to 5 TB per file
            </span>
            <span className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5" />
              Encrypted at rest
            </span>
          </div>
        </div>
      </div>

      {/* Upload queue */}
      {uploads.size > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
          {/* Queue header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700/50">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                File Queue
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {uploads.size} file{uploads.size !== 1 ? 's' : ''}
                {hasActiveUploads && ' \u2022 Uploading...'}
                {allComplete && ' \u2022 All uploads complete'}
              </p>
            </div>

            {hasPendingFiles && !hasActiveUploads && (
              <button
                onClick={handleUploadAll}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-primary-500 dark:hover:bg-primary-600 dark:focus:ring-offset-gray-900"
              >
                <UploadCloud className="h-4 w-4" />
                Upload All
              </button>
            )}
          </div>

          {/* File list */}
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {Array.from(uploads.entries()).map(([key, entry]) => (
              <div key={key} className="px-6 py-3">
                <UploadProgressCard
                  fileName={entry.file.name}
                  fileSize={entry.file.size}
                  progress={entry.progress.percent}
                  status={entry.status}
                  speed={entry.status === 'uploading' ? entry.progress.speed : undefined}
                  eta={entry.status === 'uploading' ? entry.progress.eta : undefined}
                  onCancel={
                    entry.status === 'uploading'
                      ? () => handleCancel(key)
                      : undefined
                  }
                  onRetry={
                    entry.status === 'failed'
                      ? () => handleRetry(key)
                      : undefined
                  }
                  variant="embedded"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when no files selected and upload not yet started */}
      {uploads.size === 0 && !isStarted && (
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <UploadCloud className="h-6 w-6 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            No files selected
          </p>
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            Drag files to the drop zone above or click to browse
          </p>
        </div>
      )}

      {/* Post-upload success message */}
      {allComplete && (
        <div className="mt-6 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Your files have been submitted. Our audit team will review them and notify you if additional information is required.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
